/**
 * FeedbackSupportRoute Component
 *
 * Route wrapper that conditionally configures SettingsLayout based on
 * Featurebase consent state. When consent is granted, renders a minimal
 * header (breadcrumbs only) with portal navigation and revoke controls
 * in breadcrumbActions. When not granted, renders the standard header
 * with title and description.
 *
 * Owns portal command state (portalPath/portalNonce) and passes it
 * down to FeedbackSupportPage as one-way data flow.
 */

// 1. External dependencies
import { useCallback, useEffect, useRef, useState } from 'react';
import { Home, ShieldOff } from 'lucide-react';

// 2. Workspace packages
import { Button, Popover, PopoverTrigger, PopoverContent } from '@enfomentis/ui';

// 3. Internal absolute imports
import { useFeaturebase } from '@/featurebase';
import { useIsAppFullScreen } from '@/hooks';

// 4. Parent-level imports
import { SettingsLayout } from '../SettingsLayout';

// 5. Relative imports
import { FeedbackSupportPage } from './FeedbackSupportPage';

interface FeedbackSupportRouteProps {
  /** Function to open the settings drawer (passed conditionally from Settings.tsx) */
  onOpenDrawer?: () => void;
}

export function FeedbackSupportRoute({ onOpenDrawer }: FeedbackSupportRouteProps) {
  const { consent, revokeConsent } = useFeaturebase();
  const isFullScreen = useIsAppFullScreen();
  const [isRevokeOpen, setRevokeOpen] = useState(false);

  // Portal command state — owned by Route, passed to Page as props
  const [portalPath, setPortalPath] = useState('/');
  const [portalNonce, setPortalNonce] = useState(0);

  const handleBackToPortal = useCallback(() => {
    setPortalPath('/');
    setPortalNonce((n) => n + 1);
  }, []);

  // Soft resize on fullscreen toggle so the embed iframe recalculates dimensions
  // without tearing down and re-initializing (which would lose the user's place).
  const prevFullScreenRef = useRef(isFullScreen);
  useEffect(() => {
    if (prevFullScreenRef.current === isFullScreen) return;
    prevFullScreenRef.current = isFullScreen;

    if (consent !== 'granted') return;

    requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
  }, [isFullScreen, consent]);

  if (consent === 'granted') {
    return (
      <SettingsLayout
        minimalHeader
        breadcrumbItems={[]}
        breadcrumbActions={
          <div className={`flex items-center gap-1${isFullScreen ? ' mr-12' : ''}`}>
            <Button
              variant="light"
              size="sm"
              isIconOnly
              aria-label="Back to portal home"
              onPress={handleBackToPortal}
            >
              <Home className="w-4 h-4" />
            </Button>
            <Popover isOpen={isRevokeOpen} onOpenChange={setRevokeOpen} placement="bottom-end">
              <PopoverTrigger>
                <Button
                  variant="light"
                  size="sm"
                  isIconOnly
                  aria-label="Revoke support access"
                >
                  <ShieldOff className="w-4 h-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent>
                <div className="px-1 py-2 space-y-3">
                  <p className="text-sm font-semibold text-text-primary">
                    Remove support access?
                  </p>
                  <p className="text-xs text-text-secondary">
                    This will disconnect the support portal. You can re-enable it
                    at any time.
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      color="danger"
                      variant="flat"
                      onPress={() => {
                        // Scoped cleanup: clear embed host and its descendant iframes
                        const embedHost = document.querySelector('[data-featurebase-embed]');
                        if (embedHost) {
                          embedHost.innerHTML = '';
                        }
                        revokeConsent();
                        setRevokeOpen(false);
                      }}
                    >
                      Revoke
                    </Button>
                    <Button
                      size="sm"
                      variant="light"
                      onPress={() => setRevokeOpen(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        }
        contentClassName="overflow-hidden p-0"
        {...(onOpenDrawer && { onOpenDrawer })}
      >
        <FeedbackSupportPage key={consent} portalPath={portalPath} portalNonce={portalNonce} />
      </SettingsLayout>
    );
  }

  return (
    <SettingsLayout
      title="Feedback & Support"
      description="Support channel and third-party consent"
      breadcrumbItems={[]}
      contentClassName="overflow-y-auto"
      {...(onOpenDrawer && { onOpenDrawer })}
    >
      <FeedbackSupportPage key={consent} portalPath={portalPath} portalNonce={portalNonce} />
    </SettingsLayout>
  );
}
